import React, { useCallback, useEffect, useRef } from 'react';
import { LayoutChangeEvent, StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useScrollToTop } from '@react-navigation/native';

import { AppNavBar } from '@/components/AppNavBar';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { StickyCellRendererComponent } from '@/feature/lobby/components/sticky-item/StickyCellRendererComponent';
import { StickyWrapper, useStickyScrollHandler } from '@/feature/lobby/hooks/use-sticky-translation';
import { LobbyProductSwitcherChipsHandle } from '@/feature/product-transition/components/LobbyProductSwitcherChipsHandle';
import {
    LobbyWrapper,
    productSwitcherStyles,
    useLobbyListStyles,
} from '@/feature/product-transition/components/LobbyWrapper';
import { PRODUCT_CHIPS_HEIGHT } from '@/feature/product-transition/components/ProductSwitcherChips';
import { LobbyCarousel } from '@/feature/promohub/components/LobbyCarousel';
import { WhatsNewLobbyCard } from '@/feature/whats-new/components/WhatsNewLobbyCard';
import { useWhatsNewStore } from '@/feature/whats-new/hooks/use-whats-new-store';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';

import { ArcadeGamesList } from '../components/ArcadeGamesList';

export const ArcadeLobbyScreen = () => {
    const setLobbyReady = useWhatsNewStore(state => state.actions.setLobbyReady);
    const appNavbarStickPosition = useSharedValue(0);
    const handleStickPosition = useSharedValue(0);
    const { onScroll, scrollDirection, scrollOffset, scrollVelocity } = useStickyScrollHandler();
    const { lobbyHandleHeight } = useLobbyListStyles();

    useEffect(() => {
        setLobbyReady(true);
    }, [setLobbyReady]);

    const insets = useSafeAreaInsets();

    const onNavbarLayout = useCallback(
        (event: LayoutChangeEvent) => {
            appNavbarStickPosition.value = event.nativeEvent.layout.y - insets.top;
        },
        [appNavbarStickPosition, insets.top]
    );

    const onHandleChipsLayout = useCallback(
        (event: LayoutChangeEvent) => {
            handleStickPosition.value = event.nativeEvent.layout.y + PRODUCT_CHIPS_HEIGHT + lobbyHandleHeight;
        },
        [handleStickPosition, lobbyHandleHeight]
    );

    const { height } = useWindowDimensions();

    const listRef = useRef<Animated.ScrollView>(null);
    //TODO: remove ts-ignore when upgrading react navigation to v7
    // @ts-ignore
    useScrollToTop(listRef);

    return (
        <LobbyWrapper>
            <Animated.ScrollView
                ref={listRef}
                onScroll={onScroll}
                showsVerticalScrollIndicator={showScrollIndicator}
                contentContainerStyle={[
                    productSwitcherStyles.lobbyListContentContainerStyle,
                    isWeb ? { minHeight: height + PRODUCT_CHIPS_HEIGHT } : styles.contentContainer,
                ]}
            >
                <Box zIndex={101} onLayout={onHandleChipsLayout}>
                    <LobbyProductSwitcherChipsHandle
                        handleStickPosition={handleStickPosition}
                        scrollDirection={scrollDirection}
                        scrollOffset={scrollOffset}
                        scrollVelocity={scrollVelocity}
                    />
                </Box>
                <StickyCellRendererComponent shouldBeSticky={true} style={styles.appNavBar} onLayout={onNavbarLayout}>
                    <StickyWrapper
                        scrollOffset={scrollOffset}
                        scrollVelocity={scrollVelocity}
                        initialPosition={appNavbarStickPosition}
                        scrollDirection={scrollDirection}
                    >
                        <Row width={'100%'} backgroundColor={ds.colorToken.background.neutral.base}>
                            <AppNavBar />
                            <Box height={1} style={styles.divider} />
                        </Row>
                    </StickyWrapper>
                </StickyCellRendererComponent>
                <WhatsNewLobbyCard />
                <Box pt="s14">
                    <LobbyCarousel style={styles.lobbyCarousel} />
                </Box>
                <ArcadeGamesList scrollOffset={scrollOffset} />
            </Animated.ScrollView>
        </LobbyWrapper>
    );
};

const styles = StyleSheet.create({
    divider: {
        backgroundColor: ds.colorValue.border.strong,
    },
    appNavBar: {
        zIndex: 100,
        top: 0,
    },
    lobbyCarousel: {
        paddingBottom: 28,
    },
    contentContainer: {
        minHeight: '100%',
    },
});
